// The ONE place `@excalidraw/excalidraw` is imported (TODO §21/§10.1 + §10.3).
//
// Two rules meet here, and both are easier to keep if there is exactly one door:
//
// §10.3 **Lazy.** The package is ~17MB unpacked and ~1.5MB of JS after bundling. A deck with
// no diagram must never download it, so nothing imports it statically — not the editor
// component, not the SVG renderer, not the palette. `webpack.config.js` already sets
// `splitChunks: { chunks: "all" }`, so the dynamic import below falls out as its own chunk
// with no config change. Verify from the built asset list, not from intent (§16.11).
//
// §10.1 **`window.EXCALIDRAW_ASSET_PATH` must be local.** Excalidraw fetches its scene fonts
// at runtime against this variable and falls back to a CDN when it is unset — which breaks
// the offline web build and the Electron shell (no network, and a persisted-loopback
// origin), and does so by SILENTLY substituting fonts rather than failing. It has to be set
// before the package initialises its font faces, which is why it is set here, next to the
// import it guards, rather than in `main.tsx` where it would be one refactor from being
// dropped. `webpack.config.js` copies the font directory to `/fonts`.

export type DiagramEngine = typeof import("@excalidraw/excalidraw");

// The one global Excalidraw reads, typed locally and cast at the single write below.
//
// This USED to be a `interface Window { … }` augmentation in its own ambient `.d.ts`, which
// works only for as long as that file contains no `import` or `export` — add either and it
// silently becomes a module, the augmentation disappears, and this line stops compiling with
// "EXCALIDRAW_ASSET_PATH does not exist on type Window". A one-line cast in the only file
// that touches the global has no such failure mode, and keeps the declaration next to the
// assignment it describes instead of two directories away.
interface ExcalidrawGlobals {
  EXCALIDRAW_ASSET_PATH?: string | string[];
}

let enginePromise: Promise<DiagramEngine> | null = null;

// "/" — the app is always served from its origin root (webpack `publicPath: "/"`, nginx, and
// the desktop shell's loopback server all agree), so `fonts/…` resolves to the copy webpack
// emitted. A trailing slash matters: Excalidraw resolves the font paths as relative URLs
// against this base.
const ASSET_PATH = "/";

export function loadDiagramEngine(): Promise<DiagramEngine> {
  if (!enginePromise) {
    if (typeof window !== "undefined") {
      (window as unknown as ExcalidrawGlobals).EXCALIDRAW_ASSET_PATH =
        ASSET_PATH;
    }
    enginePromise = import("@excalidraw/excalidraw");
  }
  return enginePromise;
}

// Has the chunk already been fetched? Lets a caller take a synchronous path when the engine
// happens to be warm, without making "is a diagram on screen" depend on it.
export function isDiagramEngineLoaded(): boolean {
  return enginePromise !== null;
}
